// Payments hub, split by direction. Money out holds open supplier bills
// (To pay), scheduled supplier payments, and supplier-payment history (Paid).
// Money in holds customer receipts (Received). Bill payments can also start
// from a bill row. Each paid/received register filters matched and unmatched
// rows in the address. UUID text inputs are gone — invoices, bills, and
// contacts are all pickers.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import { StatusDot } from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { ActionMenu } from "@/components/ActionMenu";
import { StatusFilter } from "@/components/StatusFilter";
import { useViewSearch } from "@/lib/view-search";
import {
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { CardField } from "@/components/RecordCard";
import {
  BillPicker,
  InvoicePicker,
  LinkedContactPicker,
} from "@/components/Pickers";
import {
  type BankAccount,
  type BankTransactionLine,
  type BillPayment,
  type Invoice,
  type InvoicePayment,
  type OrganizationScope,
  listBankAccounts,
  listBankTransactions,
  listBillPayments,
  listInvoices,
  listPayments,
  matchBillPaymentToBankTransaction,
  payBill,
  reconcileBillPayment,
  reconcilePayment,
  recordPayment,
} from "@/lib/api";
import { useOrganization } from "@/lib/organization";
import { humanizeLabel, useBillNumbers, useInvoiceNumbers } from "@/lib/labels";
import { ToPayPanel } from "./ToPayPanel";
import { ScheduledPaymentsPage } from "@/features/banking/ScheduledPaymentsPage";

function toMinor(amount: string): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

function formatDay(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString();
}

function money(cents: number): string {
  return formatMoney({ cents, currency: "USD" });
}

function receiptNumber(id: string): string {
  return `RCP-${id.slice(0, 8).toUpperCase()}`;
}

type HubTab = "to-pay" | "scheduled" | "paid" | "received";
type PayStatus = "" | "unmatched" | "matched";

function matchesPayStatus(status: string, filter: PayStatus): boolean {
  if (!filter) return true;
  const matched = status === "reconciled";
  return filter === "matched" ? matched : !matched;
}

type PendingMatch =
  | { kind: "invoice"; payment: InvoicePayment }
  | { kind: "bill"; payment: BillPayment };

/** Human reference for the confirm dialog: the bill number, or the receipt
 * plus any invoices it touches. */
function matchReference(
  pending: PendingMatch,
  invoiceNumbers: Map<string, string>,
  billNumbers: Map<string, string>,
): string {
  if (pending.kind === "bill") {
    return (
      billNumbers.get(pending.payment.bill_id) ??
      `bill payment ${pending.payment.id.slice(0, 8).toUpperCase()}`
    );
  }
  const payment = pending.payment;
  const invoiceIds = [
    ...(payment.invoice_id ? [payment.invoice_id] : []),
    ...payment.allocations.map((allocation) => allocation.invoice_id),
  ];
  const invoices = [...new Set(invoiceIds)].map(
    (id) => invoiceNumbers.get(id) ?? `invoice ${id.slice(0, 8).toUpperCase()}`,
  );
  const receipt = receiptNumber(payment.id);
  return invoices.length > 0
    ? `${receipt} against ${invoices.join(", ")}`
    : receipt;
}

export function PaymentsPage() {
  const { scope } = useOrganization();
  const [tab, setTab] = useViewSearch<HubTab>(
    "tab",
    ["to-pay", "scheduled", "paid", "received"],
    "to-pay",
  );
  const direction: "out" | "in" = tab === "received" ? "in" : "out";
  const navigate = useNavigate();
  const routerSearch = useRouterState({
    select: (state) => state.location.search,
  }) as Record<string, unknown>;
  const rawStatus = routerSearch.status;
  const status: PayStatus =
    rawStatus === "unmatched" || rawStatus === "matched" ? rawStatus : "";
  const setPayStatus = (next: PayStatus) => {
    void navigate({
      to: ".",
      search: (previous) => {
        const search = { ...previous } as Record<string, unknown>;
        if (next === "") delete search.status;
        else search.status = next;
        return search;
      },
    });
  };
  const [recordOpen, setRecordOpen] = useState(false);
  const [payBillId, setPayBillId] = useState("");
  const [billsRefreshKey, setBillsRefreshKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Received (invoice payments)
  const [payments, setPayments] = useState<InvoicePayment[] | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [payLoading, setPayLoading] = useState(true);

  // Paid (bill payments)
  const [billPayments, setBillPayments] = useState<BillPayment[] | null>(null);
  const [billError, setBillError] = useState<string | null>(null);
  const [billLoading, setBillLoading] = useState(true);

  const [reconciling, setReconciling] = useState<string | null>(null);
  const [pendingMatch, setPendingMatch] = useState<PendingMatch | null>(null);
  const [bankLines, setBankLines] = useState<BankTransactionLine[] | null>(null);
  const [bankLinesLoading, setBankLinesLoading] = useState(false);
  const [bankLinesError, setBankLinesError] = useState<string | null>(null);
  const [selectedBankLineId, setSelectedBankLineId] = useState("");
  const invoiceNumbers = useInvoiceNumbers();
  const billNumbers = useBillNumbers();

  const reloadReceived = useCallback(() => {
    setPayLoading(true);
    setPayError(null);
    listPayments()
      .then((rows) => {
        setPayments(rows);
        setPayError(null);
      })
      .catch((cause: unknown) => setPayError(toErrorMessage(cause)))
      .finally(() => setPayLoading(false));
  }, []);

  const reloadPaid = useCallback(() => {
    setBillLoading(true);
    setBillError(null);
    listBillPayments()
      .then((rows) => {
        setBillPayments(rows);
        setBillError(null);
      })
      .catch((cause: unknown) => setBillError(toErrorMessage(cause)))
      .finally(() => setBillLoading(false));
  }, []);

  useEffect(reloadReceived, [reloadReceived]);
  useEffect(reloadPaid, [reloadPaid]);

  const pendingBankPayment =
    pendingMatch?.kind === "bill" &&
    pendingMatch.payment.status === "unreconciled"
      ? pendingMatch.payment
      : null;

  useEffect(() => {
    setSelectedBankLineId("");
    if (!pendingBankPayment) {
      setBankLines(null);
      setBankLinesError(null);
      setBankLinesLoading(false);
      return;
    }
    let cancelled = false;
    setBankLines(null);
    setBankLinesError(null);
    setBankLinesLoading(true);
    listBankAccounts()
      .then((accounts: BankAccount[]) =>
        Promise.all(
          accounts.map((account) => listBankTransactions(account.id)),
        ).then((groups) =>
          groups.flat().filter((line) => line.status === "unreconciled"),
        ),
      )
      .then((rows) => {
        if (!cancelled) setBankLines(rows);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setBankLinesError(toErrorMessage(cause));
      })
      .finally(() => {
        if (!cancelled) setBankLinesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pendingBankPayment]);

  const submitReconcile = async (
    kind: "invoice" | "bill",
    paymentId: string,
    bankTransactionId?: string,
  ) => {
    setReconciling(paymentId);
    setError(null);
    try {
      if (kind === "invoice") {
        await reconcilePayment(paymentId);
        reloadReceived();
      } else if (bankTransactionId) {
        await matchBillPaymentToBankTransaction(paymentId, {
          bank_transaction_id: bankTransactionId,
        });
        reloadPaid();
      } else {
        await reconcileBillPayment(paymentId);
        reloadPaid();
      }
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setReconciling(null);
    }
  };

  const visibleReceived = (payments ?? []).filter((payment) =>
    matchesPayStatus(payment.status, status),
  );
  const visiblePaid = (billPayments ?? []).filter((payment) =>
    matchesPayStatus(payment.status, status),
  );
  const receivedCounts = {
    all: (payments ?? []).length,
    unmatched: (payments ?? []).filter((payment) =>
      matchesPayStatus(payment.status, "unmatched"),
    ).length,
    matched: (payments ?? []).filter((payment) =>
      matchesPayStatus(payment.status, "matched"),
    ).length,
  };
  const paidCounts = {
    all: (billPayments ?? []).length,
    unmatched: (billPayments ?? []).filter((payment) =>
      matchesPayStatus(payment.status, "unmatched"),
    ).length,
    matched: (billPayments ?? []).filter((payment) =>
      matchesPayStatus(payment.status, "matched"),
    ).length,
  };

  return (
    <div className="grid gap-6">
      <PageHeader
        title={direction === "in" ? "Payments received" : "Payments"}
        description={
          tab === "to-pay"
            ? "Open supplier bills waiting to be paid."
            : tab === "scheduled"
              ? "Supplier payment plans and payments made outside Accounting."
              : tab === "paid"
                ? "Money already paid to suppliers."
                : "Money received from customers. Open a receipt from its row."
        }
        actions={
          tab === "scheduled" ? undefined : (
            <>
              {tab === "to-pay" ? (
                <Button asChild variant="secondary">
                  <Link to="/banking/batches">Payment runs</Link>
                </Button>
              ) : null}
              <Button
                type="button"
                onClick={() => {
                  setPayBillId("");
                  setRecordOpen(true);
                }}
              >
                {direction === "in"
                  ? "Record received payment"
                  : "Record supplier payment"}
              </Button>
            </>
          )
        }
      />

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          {error.includes("No Console organization context") ? (
            <p className="mt-1 text-[var(--muted-foreground)]">
              Accounting follows the organization chosen in SYLVIQ Console. Pick one there and relaunch Accounting.
            </p>
          ) : null}
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice}
        </output>
      ) : null}

      {direction === "in" ? (
        <div className="grid gap-4">
          <StatusFilter
            label="Filter received payments"
            value={status}
            onChange={(value) =>
              setPayStatus(
                value === "unmatched" || value === "matched" ? value : "",
              )
            }
            options={[
              { value: "", label: "All", count: receivedCounts.all },
              {
                value: "unmatched",
                label: "Unmatched",
                count: receivedCounts.unmatched,
              },
              { value: "matched", label: "Matched", count: receivedCounts.matched },
            ]}
          />
          {payError ? (
            <ReadError message={payError} onRetry={reloadReceived} />
          ) : (
            <PaymentTable
              kind="invoice"
              loading={payLoading}
              empty={
                (payments ?? []).length === 0
                  ? "No payments received yet."
                  : "No payments match these filters."
              }
              isEmpty={visibleReceived.length === 0}
              cards={visibleReceived.map((payment) => (
                <InvoicePaymentCard
                  key={payment.id}
                  payment={payment}
                  invoiceNumbers={invoiceNumbers}
                  busy={reconciling === payment.id}
                  onMatch={() =>
                    setPendingMatch({ kind: "invoice", payment })
                  }
                />
              ))}
            >
              {visibleReceived.map((payment) => (
                <InvoicePaymentRow
                  key={payment.id}
                  payment={payment}
                  invoiceNumbers={invoiceNumbers}
                  busy={reconciling === payment.id}
                  onMatch={() =>
                    setPendingMatch({ kind: "invoice", payment })
                  }
                />
              ))}
            </PaymentTable>
          )}
        </div>
      ) : null}

      {direction === "out" ? (
        <Tabs
          label="Money out views"
          tabs={[
            { value: "to-pay", label: "To pay" },
            { value: "scheduled", label: "Scheduled" },
            { value: "paid", label: "Paid" },
          ]}
          value={tab}
          onValueChange={(value) => setTab(value as HubTab)}
        >
          {tab === "to-pay" ? (
            <ToPayPanel
              refreshKey={billsRefreshKey}
              onPay={(billId) => {
                setPayBillId(billId);
                setRecordOpen(true);
              }}
            />
          ) : null}

          {tab === "scheduled" ? <ScheduledPaymentsPage embedded /> : null}

          {tab === "paid" ? (
            <div className="grid gap-4">
              <StatusFilter
                label="Filter supplier payments"
                value={status}
                onChange={(value) =>
                  setPayStatus(
                    value === "unmatched" || value === "matched" ? value : "",
                  )
                }
                options={[
                  { value: "", label: "All", count: paidCounts.all },
                  {
                    value: "unmatched",
                    label: "Unmatched",
                    count: paidCounts.unmatched,
                  },
                  { value: "matched", label: "Matched", count: paidCounts.matched },
                ]}
              />
              {billError ? (
                <ReadError message={billError} onRetry={reloadPaid} />
              ) : (
                <PaymentTable
                  kind="bill"
                  loading={billLoading}
                  empty={
                    (billPayments ?? []).length === 0
                      ? "No supplier payments yet."
                      : "No payments match these filters."
                  }
                  isEmpty={visiblePaid.length === 0}
                  cards={visiblePaid.map((payment) => (
                    <BillPaymentCard
                      key={payment.id}
                      payment={payment}
                      billNumbers={billNumbers}
                      busy={reconciling === payment.id}
                      onMatch={() => setPendingMatch({ kind: "bill", payment })}
                    />
                  ))}
                >
                  {visiblePaid.map((payment) => (
                    <BillPaymentRow
                      key={payment.id}
                      payment={payment}
                      billNumbers={billNumbers}
                      busy={reconciling === payment.id}
                      onMatch={() => setPendingMatch({ kind: "bill", payment })}
                    />
                  ))}
                </PaymentTable>
              )}
            </div>
          ) : null}
        </Tabs>
      ) : null}

      <Dialog
        open={recordOpen}
        onOpenChange={setRecordOpen}
        title={
          tab === "received" ? "Record a payment" : "Record a supplier payment"
        }
        description={
          tab === "received"
            ? "Records money received from a customer."
            : "Pays a supplier bill."
        }
      >
        {tab === "received" ? (
          <RecordReceivedForm
            scope={scope}
            onDone={(text) => {
              setRecordOpen(false);
              setNotice(text);
              reloadReceived();
            }}
            onError={setError}
          />
        ) : (
          <RecordPaidForm
            initialBillId={payBillId}
            onDone={(text) => {
              setRecordOpen(false);
              setNotice(text);
              setPayBillId("");
              setBillsRefreshKey((key) => key + 1);
              reloadPaid();
            }}
            onError={setError}
          />
        )}
      </Dialog>

      <Dialog
        open={pendingMatch !== null}
        onOpenChange={(open) => {
          if (!open) setPendingMatch(null);
        }}
        title="Match this payment?"
        description={
          pendingMatch
            ? pendingMatch.kind === "invoice"
              ? `Matching ${matchReference(pendingMatch, invoiceNumbers, billNumbers)} (${money(pendingMatch.payment.amount_cents)}) marks this receipt matched. It picks no bank line, does not move money, and cannot be undone here.`
              : `Matching ${matchReference(pendingMatch, invoiceNumbers, billNumbers)} (${money(pendingMatch.payment.amount_cents)}) marks this supplier payment matched. You can pick an unmatched bank line, or leave that choice empty. It does not move money, and it cannot be undone here.`
            : undefined
        }
      >
        {pendingMatch?.kind === "bill" ? (
          <div className="grid gap-3">
            <p className="text-sm text-[var(--muted-foreground)]">
              Pick the imported bank line this supplier payment corresponds to,
              or match the payment without linking a bank line.
            </p>
            {bankLinesLoading ? (
              <p role="status" className="text-sm text-[var(--muted-foreground)]">
                Loading unmatched bank lines…
              </p>
            ) : bankLinesError ? (
              <p role="alert" className="text-sm text-[var(--danger)]">
                Unmatched bank lines could not be loaded: {bankLinesError}
              </p>
            ) : (
              <label
                htmlFor="payment-match-bank-line"
                className="grid gap-1 text-xs font-medium"
              >
                Bank transaction (optional)
                <select
                  id="payment-match-bank-line"
                  className="select"
                  value={selectedBankLineId}
                  onChange={(event) =>
                    setSelectedBankLineId(event.target.value)
                  }
                >
                  <option value="">
                    No bank line — just mark this payment as matched
                  </option>
                  {(bankLines ?? []).map((line) => (
                    <option key={line.id} value={line.id}>
                      {formatDay(line.date)} · {line.description} ·{" "}
                      {money(line.amount_cents)}
                    </option>
                  ))}
                </select>
                {(bankLines ?? []).length === 0 ? (
                  <span className="text-[11px] font-normal text-[var(--muted-foreground)]">
                    No unmatched imported bank lines in this organization.
                  </span>
                ) : null}
              </label>
            )}
          </div>
        ) : null}
        <div className="actions-row mt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={() => setPendingMatch(null)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              const pending = pendingMatch;
              setPendingMatch(null);
              if (pending)
                void submitReconcile(
                  pending.kind,
                  pending.payment.id,
                  pending.kind === "bill" ? selectedBankLineId : undefined,
                );
            }}
          >
            Match payment
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function ReadError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div
      role="alert"
      className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
    >
      {message}
      <div className="mt-2">
        <Button type="button" variant="secondary" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </div>
    </div>
  );
}

function MatchedDot({ matched }: { matched: boolean }) {
  return (
    <StatusDot
      tone={matched ? "success" : "idle"}
      label={matched ? "Matched" : "Unmatched"}
    />
  );
}

function InvoicePaymentReference({
  payment,
  invoiceNumbers,
}: {
  payment: InvoicePayment;
  invoiceNumbers: Map<string, string>;
}) {
  const allocated = payment.allocations.map(
    (allocation) => invoiceNumbers.get(allocation.invoice_id) ?? "Invoice",
  );
  const single = payment.invoice_id
    ? invoiceNumbers.get(payment.invoice_id)
    : undefined;
  const labels = allocated.length > 0 ? allocated : single ? [single] : [];
  return labels.length > 0 ? (
    <span className="block text-xs text-[var(--muted-foreground)]">
      {labels.join(", ")}
    </span>
  ) : (
    <span className="block text-xs text-[var(--muted-foreground)]">
      Unapplied
    </span>
  );
}

function InvoicePaymentRow({
  payment,
  invoiceNumbers,
  busy,
  showUnapplied = true,
  onMatch,
}: {
  payment: InvoicePayment;
  invoiceNumbers: Map<string, string>;
  busy: boolean;
  showUnapplied?: boolean;
  onMatch: () => void;
}) {
  return (
    <TableRow>
      <TableCell>
        <Link
          to="/receipts/$paymentId"
          params={{ paymentId: payment.id }}
          className="text-xs text-[var(--action)] underline-offset-4 hover:underline"
          title="Open the receipt for this payment"
        >
          {receiptNumber(payment.id)}
        </Link>
        <InvoicePaymentReference
          payment={payment}
          invoiceNumbers={invoiceNumbers}
        />
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {formatDay(payment.received_at)}
      </TableCell>
      <TableCell>{humanizeLabel(payment.method)}</TableCell>
      <TableCell>
        <MatchedDot matched={payment.status === "reconciled"} />
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {money(payment.amount_cents)}
      </TableCell>
      {showUnapplied ? (
        <TableCell className="text-right tabular-nums">
          {money(payment.unapplied_amount_cents)}
        </TableCell>
      ) : null}
      <TableCell>
        <div className="flex justify-end">
          <ActionMenu
            label="Actions"
            ariaLabel={`Actions for ${receiptNumber(payment.id)}`}
            items={[
              {
                id: "open-receipt",
                label: "Open receipt",
                to: "/receipts/$paymentId",
                params: { paymentId: payment.id },
              },
              ...(payment.status === "reconciled"
                ? []
                : [
                    {
                      id: "match",
                      label: "Match to books",
                      onSelect: onMatch,
                      busy,
                      busyLabel: "Matching…",
                    },
                  ]),
            ]}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

function InvoicePaymentCard({
  payment,
  invoiceNumbers,
  busy,
  showUnapplied = true,
  onMatch,
}: {
  payment: InvoicePayment;
  invoiceNumbers: Map<string, string>;
  busy: boolean;
  showUnapplied?: boolean;
  onMatch: () => void;
}) {
  const matched = payment.status === "reconciled";
  return (
    <article className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4">
      <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
        <Link
          to="/receipts/$paymentId"
          params={{ paymentId: payment.id }}
          className="record-title underline-offset-4 hover:underline"
        >
          {receiptNumber(payment.id)}
        </Link>
        <span className="tabular-nums">{money(payment.amount_cents)}</span>
      </div>
      <CardField label="Date">{formatDay(payment.received_at)}</CardField>
      <CardField label="Method">{humanizeLabel(payment.method)}</CardField>
      <CardField label="Applied to">
        <InvoicePaymentReference
          payment={payment}
          invoiceNumbers={invoiceNumbers}
        />
      </CardField>
      <CardField label="Status">
        <MatchedDot matched={matched} />
      </CardField>
      {showUnapplied ? (
        <CardField label="Unapplied">
          {money(payment.unapplied_amount_cents)}
        </CardField>
      ) : null}
      <div className="flex flex-wrap justify-end pt-3">
        <ActionMenu
          label="Actions"
          ariaLabel={`Actions for ${receiptNumber(payment.id)}`}
          items={[
            {
              id: "open-receipt",
              label: "Open receipt",
              to: "/receipts/$paymentId",
              params: { paymentId: payment.id },
            },
            ...(matched
              ? []
              : [
                  {
                    id: "match",
                    label: "Match to books",
                    onSelect: onMatch,
                    busy,
                    busyLabel: "Matching…",
                  },
                ]),
          ]}
        />
      </div>
    </article>
  );
}

function BillPaymentRow({
  payment,
  billNumbers,
  busy,
  onMatch,
}: {
  payment: BillPayment;
  billNumbers: Map<string, string>;
  busy: boolean;
  onMatch: () => void;
}) {
  return (
    <TableRow>
      <TableCell>
        <Link
          to="/bills/$billId"
          params={{ billId: payment.bill_id }}
          className="text-xs text-[var(--action)] underline-offset-4 hover:underline"
          title={payment.bill_id}
        >
          {billNumbers.get(payment.bill_id) ?? "View bill"} →
        </Link>
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {formatDay(payment.received_at)}
      </TableCell>
      <TableCell>{humanizeLabel(payment.method)}</TableCell>
      <TableCell>
        <MatchedDot matched={payment.status === "reconciled"} />
      </TableCell>
      <TableCell className="text-right tabular-nums">
        {money(payment.amount_cents)}
      </TableCell>
      <TableCell>
        <div className="flex justify-end">
          <ActionMenu
            label="Actions"
            ariaLabel={`Actions for ${billNumbers.get(payment.bill_id) ?? "bill payment"}`}
            items={[
              {
                id: "open-bill",
                label: "Open bill",
                to: "/bills/$billId",
                params: { billId: payment.bill_id },
              },
              ...(payment.status === "reconciled"
                ? []
                : [
                    {
                      id: "match",
                      label: "Match to books",
                      onSelect: onMatch,
                      busy,
                      busyLabel: "Matching…",
                    },
                  ]),
            ]}
          />
        </div>
      </TableCell>
    </TableRow>
  );
}

function BillPaymentCard({
  payment,
  billNumbers,
  busy,
  onMatch,
}: {
  payment: BillPayment;
  billNumbers: Map<string, string>;
  busy: boolean;
  onMatch: () => void;
}) {
  const matched = payment.status === "reconciled";
  const bill = billNumbers.get(payment.bill_id) ?? "Supplier bill";
  return (
    <article className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4">
      <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
        <Link
          to="/bills/$billId"
          params={{ billId: payment.bill_id }}
          className="record-title underline-offset-4 hover:underline"
          title={payment.bill_id}
        >
          {bill}
        </Link>
        <span className="tabular-nums">{money(payment.amount_cents)}</span>
      </div>
      <CardField label="Date">{formatDay(payment.received_at)}</CardField>
      <CardField label="Method">{humanizeLabel(payment.method)}</CardField>
      <CardField label="Status">
        <MatchedDot matched={matched} />
      </CardField>
      <div className="flex flex-wrap justify-end pt-3">
        <ActionMenu
          label="Actions"
          ariaLabel={`Actions for ${bill}`}
          items={[
            {
              id: "open-bill",
              label: "Open bill",
              to: "/bills/$billId",
              params: { billId: payment.bill_id },
            },
            ...(matched
              ? []
              : [
                  {
                    id: "match",
                    label: "Match to books",
                    onSelect: onMatch,
                    busy,
                    busyLabel: "Matching…",
                  },
                ]),
          ]}
        />
      </div>
    </article>
  );
}

function PaymentTable({
  kind,
  showUnapplied = true,
  loading,
  empty,
  isEmpty,
  children,
  cards,
}: {
  kind: "invoice" | "bill";
  showUnapplied?: boolean;
  loading: boolean;
  empty: string;
  isEmpty: boolean;
  children: ReactNode;
  cards: ReactNode;
}) {
  const columnCount = kind === "invoice" ? (showUnapplied ? 7 : 6) : 6;
  return (
    <>
      <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
        <div className="table-scroll">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{kind === "invoice" ? "Receipt" : "Bill"}</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                {kind === "invoice" && showUnapplied ? (
                  <TableHead className="text-right">Unapplied</TableHead>
                ) : null}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columnCount}>Loading…</TableCell>
                </TableRow>
              ) : isEmpty ? (
                <TableRow>
                  <TableCell colSpan={columnCount}>{empty}</TableCell>
                </TableRow>
              ) : (
                children
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <div
        className="stack-list grid gap-3 min-[64rem]:hidden"
        data-testid={`payments-${kind}-stack`}
      >
        {loading ? (
          <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
        ) : isEmpty ? (
          <p className="text-sm text-[var(--muted-foreground)]">{empty}</p>
        ) : (
          cards
        )}
      </div>
    </>
  );
}

function PaymentAccountSelect({
  id,
  accounts,
  value,
  onChange,
  disabled,
  includeCards,
}: {
  id: string;
  accounts: BankAccount[];
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  includeCards: boolean;
}) {
  const options = accounts.filter((account) => {
    if (!(account.is_active ?? account.active ?? true)) return false;
    return includeCards
      ? account.account_type !== "loan"
      : account.account_type !== "credit_card" && account.account_type !== "loan";
  });
  return (
    <label htmlFor={id} className="grid gap-1 text-xs font-medium">
      Account
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        className="h-9 rounded-md border border-[var(--border)] bg-[var(--background)] px-2 text-sm"
      >
        <option value="">Default bank account</option>
        {options.map((account) => (
          <option key={account.id} value={account.id}>
            {account.account_name} ({account.account_type.replaceAll("_", " ")})
          </option>
        ))}
      </select>
      <span className="text-[11px] font-normal text-[var(--muted-foreground)]">
        Leave this blank to use the default bank account in the books.
      </span>
    </label>
  );
}

/** Record a customer payment: invoice picker (optionally narrowed by linked
 * contact), amount, method, date. */
function RecordReceivedForm({
  scope,
  onDone,
  onError,
}: {
  scope: OrganizationScope | null;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [contactId, setContactId] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [amount, setAmount] = useState("");
  const [applyNow, setApplyNow] = useState("");
  const [method, setMethod] = useState("Bank transfer");
  const [receivedAt, setReceivedAt] = useState(todayInput);
  const [bankAccountId, setBankAccountId] = useState("");
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [recording, setRecording] = useState(false);
  const pendingSubmission = useRef<{ fingerprint: string; key: string } | null>(null);

  useEffect(() => {
    listBankAccounts()
      .then(setBankAccounts)
      .catch(() => setBankAccounts([]));
  }, []);

  // Narrow the invoice picker to the linked contact's customer name when the
  // invoice carries that contact id.
  useEffect(() => {
    let cancelled = false;
    setInvoiceId("");
    setApplyNow("");
    if (!scope?.legal_entity_id) {
      setInvoices([]);
      return () => {
        cancelled = true;
      };
    }
    listInvoices()
      .then((invoices) => {
        if (cancelled) return;
        setInvoices(
          invoices.filter(
            (invoice) => invoice.legal_entity_id === scope.legal_entity_id,
          ),
        );
      })
      .catch(() => {
        // Picker still works unfiltered; narrowing is a convenience.
      });
    return () => {
      cancelled = true;
    };
  }, [scope?.organization_id, scope?.legal_entity_id]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setRecording(true);
    try {
      const amount_minor = toMinor(amount);
      if (amount_minor === null)
        throw new Error("Payment amount must be a positive number.");
      if (!receivedAt) throw new Error("Received date is required.");
      if (!method.trim()) throw new Error("Payment method is required.");
      if (!scope?.legal_entity_id) throw new Error("No active legal entity scope.");
      const selectedInvoice = invoices.find((invoice) => invoice.id === invoiceId);
      const crm_contact_id = contactId || selectedInvoice?.crm_contact_id || undefined;
      const apply_minor = invoiceId
        ? applyNow.trim()
          ? toMinor(applyNow)
          : amount_minor
        : 0;
      if (invoiceId && !selectedInvoice) throw new Error("Choose a valid invoice.");
      if (invoiceId && applyNow.trim() && apply_minor === null)
        throw new Error("Amount to apply must be a positive number.");
      if (apply_minor !== null && apply_minor > amount_minor)
        throw new Error("Amount applied now cannot exceed cash received.");
      if (!crm_contact_id)
        throw new Error("Choose a customer before recording a receipt.");
      const allocations = invoiceId && apply_minor
        ? [{ invoice_id: invoiceId, amount_minor: apply_minor }]
        : [];
      const payload = {
        ...(selectedInvoice ? { legal_entity_id: scope.legal_entity_id } : {}),
        crm_contact_id,
        amount_minor,
        method: method.trim(),
        received_at: new Date(`${receivedAt}T00:00:00`).toISOString(),
        ...(bankAccountId ? { bank_account_id: bankAccountId } : {}),
        allocations,
      };
      const fingerprint = JSON.stringify({
        organization_id: scope.organization_id,
        legal_entity_id: scope.legal_entity_id,
        payload,
      });
      const pending = pendingSubmission.current?.fingerprint === fingerprint
        ? pendingSubmission.current
        : { fingerprint, key: globalThis.crypto.randomUUID() };
      pendingSubmission.current = pending;
      const result = await recordPayment({
        ...payload,
        idempotency_key: pending.key,
      });
      onDone(
        `Recorded ${formatMoney({ cents: result.amount_cents, currency: "USD" })}; ${formatMoney({ cents: result.unapplied_amount_cents, currency: "USD" })} remains unapplied.`,
      );
      pendingSubmission.current = null;
      setInvoiceId("");
      setApplyNow("");
      setAmount("");
    } catch (cause) {
      onError(toErrorMessage(cause));
    } finally {
      setRecording(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="surface grid gap-3 rounded-2xl p-5"
      aria-label="Record a received payment"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-1 text-xs font-medium">
          <span id="pay-contact-label">Customer contact</span>
          <LinkedContactPicker
            id="pay-contact"
            value={contactId}
            onChange={(next) => {
              setContactId(next);
              setInvoiceId("");
              setApplyNow("");
            }}
            disabled={recording}
          />
        </div>
        <div className="grid gap-1 text-xs font-medium">
          <label htmlFor="pay-invoice">Apply to invoice now (optional)</label>
          <InvoicePicker
            id="pay-invoice"
            value={invoiceId}
            onChange={setInvoiceId}
            disabled={recording}
            crmContactId={contactId || undefined}
            legalEntityId={scope?.legal_entity_id}
          />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] sm:items-end">
        <label htmlFor="pay-amount" className="grid gap-1 text-xs font-medium">
          Cash received (USD)
          <Input
            id="pay-amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            placeholder="0.00"
            step="0.01"
            min="0"
            inputMode="decimal"
            disabled={recording}
          />
        </label>
        <label htmlFor="pay-apply-now" className="grid gap-1 text-xs font-medium">
          Apply now (USD)
          <Input
            id="pay-apply-now"
            type="number"
            value={applyNow}
            onChange={(e) => setApplyNow(e.target.value)}
            placeholder={invoiceId ? "Full receipt" : "—"}
            step="0.01"
            min="0"
            inputMode="decimal"
            disabled={recording || !invoiceId}
          />
        </label>
        <label htmlFor="pay-method" className="grid gap-1 text-xs font-medium">
          Method
          <Input
            id="pay-method"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            required
            placeholder="Bank transfer"
            disabled={recording}
          />
        </label>
        <label htmlFor="pay-date" className="grid gap-1 text-xs font-medium">
          Received on
          <Input
            id="pay-date"
            type="date"
            value={receivedAt}
            onChange={(e) => setReceivedAt(e.target.value)}
            required
            disabled={recording}
          />
        </label>
        <PaymentAccountSelect
          id="pay-account"
          accounts={bankAccounts}
          value={bankAccountId}
          onChange={setBankAccountId}
          disabled={recording}
          includeCards={false}
        />
        <Button type="submit" disabled={recording}>
          {recording ? "Recording…" : "Record payment"}
        </Button>
      </div>
      <p className="text-xs text-[var(--muted-foreground)]">
        Leave the invoice blank to hold the receipt as unapplied AR credit. A linked CRM contact is required for unapplied or partially applied cash.
      </p>
    </form>
  );
}

/** Record a supplier payment against a picked bill (previously only possible
 * from the Bills page). */
function RecordPaidForm({
  initialBillId = "",
  onDone,
  onError,
}: {
  initialBillId?: string;
  onDone: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [billId, setBillId] = useState(initialBillId);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Bank transfer");
  const [paidAt, setPaidAt] = useState(todayInput);
  const [bankAccountId, setBankAccountId] = useState("");
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    setBillId(initialBillId);
  }, [initialBillId]);

  useEffect(() => {
    listBankAccounts()
      .then(setBankAccounts)
      .catch(() => setBankAccounts([]));
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPaying(true);
    try {
      if (!billId) throw new Error("Pick the bill being paid.");
      const amount_minor = toMinor(amount);
      if (amount_minor === null)
        throw new Error("Payment amount must be a positive number.");
      if (!paidAt) throw new Error("Paid date is required.");
      if (!method.trim()) throw new Error("Payment method is required.");
      const result = await payBill(billId, {
        amount_minor,
        method: method.trim(),
        received_at: new Date(`${paidAt}T00:00:00`).toISOString(),
        ...(bankAccountId ? { bank_account_id: bankAccountId } : {}),
      });
      onDone(
        `Paid ${formatMoney({ cents: result.amount_cents, currency: "USD" })} against the bill — see the Bills page for its updated balance.`,
      );
      setBillId("");
      setAmount("");
    } catch (cause) {
      onError(toErrorMessage(cause));
    } finally {
      setPaying(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="surface grid gap-3 rounded-2xl p-5"
      aria-label="Record a supplier payment"
    >
      <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr_1fr_1fr_auto] sm:items-end">
        <div className="grid gap-1 text-xs font-medium">
          <label htmlFor="paid-bill">Bill</label>
          <BillPicker
            id="paid-bill"
            value={billId}
            onChange={setBillId}
            disabled={paying}
            required
          />
        </div>
        <label htmlFor="paid-amount" className="grid gap-1 text-xs font-medium">
          Amount (USD)
          <Input
            id="paid-amount"
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
            placeholder="0.00"
            step="0.01"
            min="0"
            inputMode="decimal"
            disabled={paying}
          />
        </label>
        <label htmlFor="paid-method" className="grid gap-1 text-xs font-medium">
          Method
          <Input
            id="paid-method"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            required
            placeholder="Bank transfer"
            disabled={paying}
          />
        </label>
        <label htmlFor="paid-date" className="grid gap-1 text-xs font-medium">
          Paid on
          <Input
            id="paid-date"
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.target.value)}
            required
            disabled={paying}
          />
        </label>
        <PaymentAccountSelect
          id="paid-account"
          accounts={bankAccounts}
          value={bankAccountId}
          onChange={setBankAccountId}
          disabled={paying}
          includeCards
        />
        <Button type="submit" disabled={paying}>
          {paying ? "Recording…" : "Record payment"}
        </Button>
      </div>
      <p className="text-xs text-[var(--muted-foreground)]">
        Supplier payments can also be recorded from the{" "}
        <Link to="/bills" className="underline">
          Bills page
        </Link>
        .
      </p>
    </form>
  );
}
